import { clientFor, type PageQuery, type PageResult } from '@yc/api-client'

import { toRFC3339 } from './datetime'
import { listQuery } from './query'

const api = clientFor('/scr/api')

/** 预警级别：与后端 internal/alert/service.go 的 Levels 同源两份 */
export type AlertLevel = 'info' | 'warn' | 'critical'

/** 预警状态机（spec §5.1）：open → processing → resolved */
export type AlertStatus = 'open' | 'processing' | 'resolved'

/** 来源模块：ref_no 存的是这个模块的单号（跨库只靠字符串，不建外键） */
export type AlertSourceModule = 'wms' | 'ops' | 'dsp' | 'pod' | 'mtm' | 'manual'

/** 预警级动作，与后端 POST /alerts/:id/process|resolve 及详情内建响应一一对应 */
export type AlertAction = 'process' | 'resolve'

/** 一条预警。字段与 services/scr/internal/alert/model.go 的 Alert 一一对应。 */
export interface Alert {
  id: number
  tenant_id: number
  /** 预警单号，前缀 YJ */
  doc_no: string
  level: AlertLevel
  source_module: AlertSourceModule
  /** 关联的外部单号（wms 出库单 / ops 订单 / dsp 行车任务 / pod 运单 / mtm 计划），不跨库校验 */
  ref_no: string
  title: string
  detail: string
  status: AlertStatus
  /** 异常发生时间，后端建单必填 */
  occurred_at: string
  /** 后端 json tag 带 omitempty，空值时字段直接不出现 */
  handled_by?: string
  handled_at?: string | null
  closed_at?: string | null
  created_at: string
  updated_at: string
}

/** 列表行与详情都是 Alert 本身（AlertListRow / AlertDetail 只是内嵌） */
export type AlertRow = Alert
export type AlertDetail = Alert

/** 建单入参：status 由服务端置 open，单号由服务端生成，都不在入参里 */
export interface AlertInput {
  level: AlertLevel
  source_module: AlertSourceModule
  title: string
  occurred_at: string
  ref_no?: string
  detail?: string
}

/** 受理入参：handled_by 落 handled_by 列，前端必填 */
export interface AlertProcessInput {
  handled_by: string
}

/** 解决入参：后端把它追加到 detail 末尾（"[解决] ..."），可留空 */
export interface AlertResolveInput {
  detail?: string
}

export interface AlertQuery extends PageQuery {
  keyword?: string
  status?: string
  level?: string
  source_module?: string
}

/** 表单态：空文本字段用 ''，没选时间用 null */
export interface AlertForm {
  level: AlertLevel
  source_module: AlertSourceModule
  title: string
  occurred_at: string | null
  ref_no: string
  detail: string
}

/** 级别字典，顺序与后端 alert.Levels 一致，即前端下拉的展示顺序 */
export const LEVELS: AlertLevel[] = ['info', 'warn', 'critical']

/** 来源模块字典，顺序与后端 validateInput 的 mods 一致 */
export const SOURCE_MODULES: AlertSourceModule[] = ['wms', 'ops', 'dsp', 'pod', 'mtm', 'manual']

/** 与后端 services/scr/internal/alert/service.go 的 StatusNames 同源两份 */
export const STATUS_LABELS: Record<AlertStatus, string> = {
  open: '待处理',
  processing: '处理中',
  resolved: '已解决',
}

/** 与后端 internal/alert/service.go 的 Levels 同源两份 */
export const LEVEL_LABELS: Record<AlertLevel, string> = {
  info: '提示',
  warn: '警告',
  critical: '严重',
}

/** 来源模块没有后端文案字典（validateInput 里只是一张白名单 map），文案归前端 */
export const SOURCE_MODULE_LABELS: Record<AlertSourceModule, string> = {
  wms: '仓储 WMS',
  ops: '运营 OPS',
  dsp: '调度 DSP',
  pod: '签收 POD',
  mtm: '联运 MTM',
  manual: '手工上报',
}

/** 状态 → 表格里的标签色 */
export const STATUS_TAG: Record<AlertStatus, 'info' | 'primary' | 'warning' | 'success' | 'danger'> =
  {
    open: 'danger',
    processing: 'warning',
    resolved: 'success',
  }

/** 级别 → 表格里的标签色 */
export const LEVEL_TAG: Record<AlertLevel, 'info' | 'warning' | 'danger'> = {
  info: 'info',
  warn: 'warning',
  critical: 'danger',
}

/**
 * 各状态允许的动作，抄后端 internal/alert/service.go 的状态机：
 * open 才能受理（受理写 handled_by/handled_at），processing 才能解决（解决写 closed_at）。
 */
const ACTIONS: Record<AlertStatus, AlertAction[]> = {
  open: ['process'],
  processing: ['resolve'],
  resolved: [],
}

/** 状态能不能触发某个动作（前端只做展示，最终以后端 409 为准） */
export function canAct(status: AlertStatus, action: AlertAction): boolean {
  return ACTIONS[status].includes(action)
}

/** 表单 → 请求体：空文本不进 body（undefined 在 JSON.stringify 里会被丢掉） */
export function alertInput(form: AlertForm): AlertInput {
  const input: AlertInput = {
    level: form.level,
    source_module: form.source_module,
    title: form.title.trim(),
    // occurred_at 后端必填（零值 422），表单校验已保证选了时间
    occurred_at: toRFC3339(form.occurred_at) ?? '',
  }
  const refNo = form.ref_no.trim()
  if (refNo) input.ref_no = refNo
  const detail = form.detail.trim()
  if (detail) input.detail = detail
  return input
}

export const listAlerts = (q: AlertQuery): Promise<PageResult<AlertRow>> =>
  api.get<PageResult<AlertRow>>('/alerts', listQuery({ ...q }))

export const getAlert = (id: number): Promise<AlertDetail> => api.get<AlertDetail>(`/alerts/${id}`)

export const createAlert = (input: AlertInput): Promise<Alert> => api.post<Alert>('/alerts', input)

/** 受理：open → processing。body 不能是空——后端 ShouldBindJSON 收到空 body 会 422 */
export const processAlert = (id: number, input: AlertProcessInput): Promise<Alert> =>
  api.post<Alert>(`/alerts/${id}/process`, input)

/** 解决：processing → resolved，detail 追加到原 detail 末尾 */
export const resolveAlert = (id: number, input: AlertResolveInput = {}): Promise<Alert> =>
  api.post<Alert>(`/alerts/${id}/resolve`, { ...input })

/** 下拉参照用的全量预警：后端没有 size 上限，这里按 200 取，与其他子系统的参照口径一致 */
export const listAlertOptions = (): Promise<Alert[]> =>
  listAlerts({ page: 1, size: 200 }).then((page) => page.list)
